import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from '@alltrails/shared/react-intl';

import TextInput from '@alltrails/shared/denali/components/TextInput';
import Button from '@alltrails/shared/denali/components/Button';
import Check from '@alltrails/shared/icons/Check';
import TextArea from '@alltrails/shared/denali/components/TextArea';
import Radio from '@alltrails/shared/denali/components/Radio';
import Select from '@alltrails/shared/denali/components/Select';
import isEmail from 'validator/lib/isEmail';
import { Context } from '@alltrails/shared/types/context';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import ProfileUser from 'types/ProfileUser';
import CustomProvider from 'components/CustomProvider';
import UserAvatar from '../../profile/UserAvatar';
import AlgoliaSearchBox from '../../AlgoliaSearchBox';
import DatePicker from '../../shared/DatePicker/DatePicker';
import { loadAndExecuteRecaptcha } from '../../../utils/recaptcha_helpers';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { EDIT_PROFILE } from '../../../utils/constants/SearchAnalyticsConstants';

const CM_IN_INCH = 2.54;
const KG_IN_LB = 0.45359237;

type Activity = {
  name: string;
  uid: string;
};

type ProfileFormUser = {
  aboutMe?: string;
  activities?: string[];
  birthday?: Date;
  city?: string;
  country?: {
    id?: number;
    isoCode?: string;
    latitude?: string;
    longitude?: string;
    name?: string;
    slug?: string;
  };
  displaySpeed?: boolean;
  email?: string;
  firstName?: string;
  height?: string | number;
  language?: string;
  lastName?: string;
  metric?: boolean;
  private?: boolean;
  sex?: string;
  state?: string;
  weight?: string | number;
  algoliaObjectId?: any;
  password?: string;
  recaptcha?: string;
};

type EnabledLanguages = {
  language_region_code: string;
  name: string;
};

type Errors = {
  firstName?: string[];
  lastName?: string[];
  password?: string[];
  email?: string[];
  formError?: string[];
};

type Props = {
  context: Context;
  currentUser: ProfileFormUser;
  user: ProfileUser;
  activities: Activity[];
  enabledLanguages: EnabledLanguages[];
  defaultLatLng: string[];
  mobileBrowser: boolean;
};

const messages = defineMessages({
  REQUIRED: { defaultMessage: 'Required' },
  AT_LEAST_SIX_CHARS: { defaultMessage: 'Must be at least 6 character' },
  NO_LOCATION: { defaultMessage: 'No current location info' },
  MEMBER_LOCATION: { defaultMessage: 'Member location: ' },
  FIRST_NAME: { defaultMessage: 'First name' },
  LAST_NAME: { defaultMessage: 'Last name' },
  SET_NEW_PASSWORD: { defaultMessage: 'Set a new password' },
  EMAIL: { defaultMessage: 'Email' },
  ABOUT_ME: { defaultMessage: 'About me' },
  FAVORITE_ACTIVITIES: { defaultMessage: 'Favorite activities' },
  UNITS: { defaultMessage: 'Units' },
  IMPERIAL: { defaultMessage: 'Imperial' },
  METRIC: { defaultMessage: 'Metric' },
  CANCEL: { defaultMessage: 'Cancel' },
  SAVE: { defaultMessage: 'Save' },
  CHANGE_LOCATION: { defaultMessage: 'Change member location' },
  RECORDING_PREFERENCE: { defaultMessage: 'Activity time preference' },
  SPEED: { defaultMessage: 'Speed' },
  PACE: { defaultMessage: 'Pace' },
  UNSPECIFIED: { defaultMessage: 'unspecified' },
  HEIGHT: { defaultMessage: 'Height' },
  WEIGHT: { defaultMessage: 'Weight' },
  BIRTHDAY: { defaultMessage: 'Birthday ' },
  CALORIE_INFO: { defaultMessage: 'Calorie counter info' },
  LANGUAGE: { defaultMessage: 'Marketing language ' },
  NOT_ALLOWED: { defaultMessage: '@ is not allowed' },
  NOT_VALID_EMAIL: { defaultMessage: 'Email is not valid' }
});

const ProfileEditForm = ({ context, currentUser, user, activities, enabledLanguages, defaultLatLng, mobileBrowser }: Props) => {
  const {
    formattedDefaultMessages: {
      REQUIRED,
      AT_LEAST_SIX_CHARS,
      NO_LOCATION,
      MEMBER_LOCATION,
      FIRST_NAME,
      LAST_NAME,
      SET_NEW_PASSWORD,
      EMAIL,
      ABOUT_ME,
      FAVORITE_ACTIVITIES,
      UNITS,
      IMPERIAL,
      METRIC,
      CANCEL,
      SAVE,
      CHANGE_LOCATION,
      RECORDING_PREFERENCE,
      SPEED,
      PACE,
      UNSPECIFIED,
      HEIGHT,
      WEIGHT,
      BIRTHDAY,
      CALORIE_INFO,
      LANGUAGE,
      NOT_ALLOWED,
      NOT_VALID_EMAIL
    }
  } = useFormatMessage(messages);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [profileFormUser, setProfileFormUser] = useState<ProfileFormUser>(currentUser);
  const [errors, setErrors] = useState<Errors>(null);
  const [newPassword, setNewPassword] = useState('');
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();

  useEffect(() => {
    if (!recaptchaToken) {
      loadAndExecuteRecaptcha('editProfile', (token: string) => {
        setRecaptchaToken(token);
      });
    }
  }, [recaptchaToken]);

  const presubmitErrors = () => {
    const newErrors = {} as Errors;
    if (!(profileFormUser?.firstName.length > 0)) {
      newErrors.firstName = [REQUIRED];
    }
    if (profileFormUser?.firstName && profileFormUser?.firstName.indexOf('@') >= 0) {
      newErrors.firstName = [NOT_ALLOWED];
    }
    if (profileFormUser.lastName && profileFormUser.lastName.indexOf('@') >= 0) {
      newErrors.lastName = [NOT_ALLOWED];
    }

    if (profileFormUser?.email && !isEmail(profileFormUser.email)) {
      newErrors.email = [NOT_VALID_EMAIL];
    }
    if (newPassword && newPassword !== '' && newPassword.length < 6) {
      newErrors.password = [AT_LEAST_SIX_CHARS];
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return true;
    }
    setErrors(null);
  };

  const userForSave = () => {
    const userForSave = profileFormUser;
    const password = newPassword;
    if (password && password !== '') userForSave.password = password;
    userForSave.recaptcha = recaptchaToken;
    return userForSave;
  };

  const handleErrors = (err: any) => {
    const { newErrors } = JSON.parse(err.responseText);
    if (newErrors) {
      setErrors(Object.assign(newErrors, errors));
    } else {
      window.location.href = LanguageSupportUtil.wrapUrlSafe('/my/profile/edit?edit_fail=true', languageRegionCode);
    }
  };

  const handleSubmit = () => {
    if (!recaptchaToken) {
      return;
    }

    const sumbitErrors = presubmitErrors();
    if (sumbitErrors) {
      return;
    }

    ServerCommunicationUtil.putApiEndpoint(
      '/my/profile',
      { user: userForSave() },
      () => {
        window.location.href = LanguageSupportUtil.wrapUrlSafe('/my/profile?edit_success=true', languageRegionCode);
      },
      (err: any) => {
        handleErrors(err);
      },
      () => {
        // complete
      }
    );
  };

  const handlePlaceSelection = (algoliaObj: any) => {
    let city;
    let state;
    let country;
    if (algoliaObj.objectID.indexOf('country') > -1) {
      country = {
        id: algoliaObj.ID,
        name: algoliaObj.name
      };
    } else if (algoliaObj.objectID.indexOf('state') > -1) {
      state = algoliaObj.name;
      country = {
        id: algoliaObj.country_id,
        name: algoliaObj.country_name
      };
    } else if (algoliaObj.objectID.indexOf('cityo') > -1) {
      city = algoliaObj.name;
      state = algoliaObj.state_name;
      country = {
        id: algoliaObj.country_id,
        name: algoliaObj.country_name
      };
    }
    profileFormUser.city = city;
    profileFormUser.state = state;
    profileFormUser.country = country;
    profileFormUser.algoliaObjectId = algoliaObj.objectID;

    setProfileFormUser({ ...profileFormUser });
  };

  const handleUserValues = (value: any, key: string) => {
    let height = null;
    let weight = null;
    switch (key) {
      case 'imperial':
        profileFormUser.metric = !value;
        break;
      case 'metric':
      case 'displaySpeed':
        profileFormUser[key] = !!value;
        break;
      case 'pace':
        profileFormUser.displaySpeed = !value;
        break;
      case 'height':
        if (value === '') {
          height = null;
        } else if (profileFormUser.metric) {
          height = value;
        } else {
          height = value * CM_IN_INCH;
        }
        profileFormUser.height = height;
        break;
      case 'weight':
        if (value === '') {
          weight = null;
        } else if (profileFormUser.metric) {
          weight = value;
        } else {
          weight = value * KG_IN_LB;
        }
        profileFormUser.weight = weight;
        break;
      default:
        (profileFormUser as any)[key] = value;
    }

    setProfileFormUser({ ...profileFormUser });
  };

  const handleNewPasswordChange = (password: string) => {
    setNewPassword(password);
  };

  const handleDateChange = (newDate: Date) => {
    profileFormUser.birthday = newDate;

    setProfileFormUser({ ...profileFormUser });
  };

  const handleAttributeSelection = (activity: Activity) => {
    const index = profileFormUser.activities.indexOf(activity.uid);
    if (index > -1) {
      profileFormUser.activities.splice(index, 1);
    } else {
      profileFormUser.activities.push(activity.uid);
    }

    setProfileFormUser({ ...profileFormUser });
  };

  const createActivityInput = (activity: Activity, checkedAttributes: any) => {
    const isSelected = checkedAttributes?.indexOf(activity.uid) >= 0;
    return (
      <div className="activities-select" key={activity.name}>
        <Button
          className="activity-tag"
          text={<FormattedMessage defaultMessage="{activity}" values={{ activity: activity.name.toLowerCase() }} />}
          onClick={() => handleAttributeSelection(activity)}
          testId={activity.name}
          variant={isSelected ? 'primary' : 'default'}
          size="sm"
          icon={isSelected ? { Component: Check } : null}
        />
      </div>
    );
  };

  const createLocationLabelText = () => {
    let text = NO_LOCATION;
    let includePrefix = false;
    const { country } = profileFormUser;
    if (country) {
      includePrefix = true;
      if (country.id === 313) {
        text = '';
      } else {
        text = country.name;
      }
      if (profileFormUser.state) {
        text = country.id === 313 ? profileFormUser.state : `${profileFormUser.state}, ${text}`;
      }
      if (profileFormUser.city) {
        text = `${profileFormUser.city}, ${text}`;
      }
    }

    if (includePrefix) text = `${MEMBER_LOCATION} ${text}`;

    return text;
  };

  const textInputComponent = (field: string) => {
    let errorMessage = '';
    let divClasses = 'input string required name-section';
    if ((errors as any)?.[field]) {
      divClasses += ' field_with_errors';
      errorMessage = (errors as any)?.[field][0];
    }
    let text;
    switch (field) {
      case 'firstName':
        text = FIRST_NAME;
        break;
      case 'lastName':
        text = LAST_NAME;
        break;
      default:
        text = EMAIL;
    }
    return (
      <div className={divClasses}>
        <TextInput
          type="text"
          placeholder={text}
          labelText={text}
          value={(profileFormUser as any)[field]}
          testId={`user_${field}`}
          errorText={errorMessage || ''}
          onChange={value => handleUserValues(value, field)}
          required
        />
      </div>
    );
  };

  const passwordDiv = () => {
    let errorMessage = '';
    let divClasses = 'input password optional bottom-section';
    if (errors?.password) {
      divClasses += ' field_with_errors';
      errorMessage = errors?.password[0];
    }

    return (
      <div className="bottom-section">
        <div className={divClasses}>
          <TextInput
            type="password"
            placeholder={SET_NEW_PASSWORD}
            labelText={SET_NEW_PASSWORD}
            value={newPassword}
            testId="user_password"
            errorText={errorMessage || ''}
            onChange={value => handleNewPasswordChange(value)}
          />
        </div>
      </div>
    );
  };

  const inchesToString = (inches: number) => {
    const ft = Math.floor(inches / 12);
    const inch = Math.round(inches - ft * 12);
    return `${ft}' ${inch}\"`;
  };

  const calorieDiv = () => {
    const { metric } = profileFormUser;
    let defaultHeight = Number(profileFormUser.height);
    defaultHeight = Math.round(metric ? defaultHeight : defaultHeight / CM_IN_INCH);
    let defaultWeight = Number(profileFormUser.weight);
    defaultWeight = Math.round(metric ? defaultWeight : defaultWeight / KG_IN_LB);
    const heightOptions = [{ label: `${UNSPECIFIED}`, value: '' }];
    const weightOptions = [{ label: `${UNSPECIFIED}`, value: '' }];
    let height;
    let initHeight;
    let endHeight;
    let weight;
    let initWeight;
    let endWeight;
    if (metric) {
      initHeight = 100;
      endHeight = 300;
      initWeight = 23;
      endWeight = 408;
      for (height = initHeight; height < endHeight; height++) {
        heightOptions.push({ label: `${height} cm`, value: String(height) });
      }
      for (weight = initWeight; weight < endWeight; weight++) {
        weightOptions.push({ label: `${weight} kg`, value: String(weight) });
      }
    } else {
      initHeight = 36;
      endHeight = 120;
      initWeight = 50;
      endWeight = 900;
      for (height = initHeight; height < endHeight; height++) {
        heightOptions.push({ label: inchesToString(height), value: String(height) });
      }
      for (weight = initWeight; weight < endWeight; weight++) {
        weightOptions.push({ label: `${weight} lbs`, value: String(weight) });
      }
    }

    return (
      <div className="calorie-counter-info clear clearfix bottom-section">
        <label className="bold-label string optional">{CALORIE_INFO}</label>
        <div id="calorie-counter-info">
          <div className="calorie-container">
            <div className="flex-row">
              <div className="calorie-section">
                <Select
                  labelText={HEIGHT}
                  onChange={value => handleUserValues(value, 'height')}
                  options={heightOptions}
                  testId="user-height-select"
                  placeholder={HEIGHT}
                  value={String(defaultHeight)}
                />
              </div>
              <div className="calorie-section">
                <Select
                  labelText={WEIGHT}
                  onChange={value => handleUserValues(value, 'weight')}
                  options={weightOptions}
                  testId="user-weight-select"
                  placeholder={WEIGHT}
                  value={String(defaultWeight)}
                />
              </div>
            </div>
            <div className="birthday-container">
              <div className="birthday-label">{BIRTHDAY}</div>
              <DatePicker date={profileFormUser.birthday} onChange={handleDateChange} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLanguageBlock = () => {
    const selectLanguageOptions = enabledLanguages.map(language => ({
      label: language.name,
      value: language.language_region_code
    }));

    let defaultVal = 'en-US';
    if (currentUser.language) {
      defaultVal = currentUser.language;
    }

    return (
      <div className="clear clearfix bottom-section">
        <Select
          labelText={LANGUAGE}
          onChange={value => handleUserValues(value, 'language')}
          options={selectLanguageOptions}
          testId="user-language-select"
          placeholder={LANGUAGE}
          value={defaultVal}
        />
      </div>
    );
  };

  const activitiesOptions = [] as JSX.Element[];
  activities.forEach((item, i) => {
    activitiesOptions.push(createActivityInput(item, profileFormUser.activities));
  });

  const formErrorMessage = errors?.formError && (
    <div className="submit-buttons">
      <span className="error">{errors.formError[0]}</span>
    </div>
  );

  return (
    <div className="edit-profile-container-form">
      <div className="small-content">
        <div className="avatar-container">
          <UserAvatar user={{ user }} publicProfile={false} />
        </div>
      </div>
      <div className="large-content">
        <div className="firstLastName">
          {textInputComponent('firstName')}
          {textInputComponent('lastName')}
        </div>
        {textInputComponent('email')}
        <div className="bottom-section">
          <TextArea
            labelText={ABOUT_ME}
            testId="user_about_me"
            placeholder={ABOUT_ME}
            rows={3}
            value={profileFormUser.aboutMe || ''}
            onChange={value => handleUserValues(value, 'aboutMe')}
          />
        </div>
        <div className="clear clearfix bottom-section">
          <div id="activities" className="filter">
            <dl className="activities light">
              <label className="bold-label">{FAVORITE_ACTIVITIES}</label>
              <div className="activities-options">{activitiesOptions}</div>
            </dl>
          </div>
        </div>
        <div className="clear bottom-section">
          <label className="bold-label string optional xlate-google">{createLocationLabelText()}</label>
          <div className="algolia-search-box">
            <AlgoliaSearchBox
              context={context}
              currentUser={currentUser}
              mobileBrowser={mobileBrowser}
              languageRegionCode={languageRegionCode}
              typeSubset={['place']}
              placeholderText={CHANGE_LOCATION}
              handleLocationSelect={handlePlaceSelection}
              defaultLatLng={defaultLatLng}
              page={EDIT_PROFILE}
              intl={intl}
            />
          </div>
        </div>
        <div className="clear clearfix bottom-section">
          <label className="bold-label string optional">{UNITS}</label>
          <div className="radio-container">
            <div className="radio-group">
              <Radio
                testId="user_imperial"
                labelElement={<span>{IMPERIAL}</span>}
                value="1"
                selected={!profileFormUser.metric}
                onChange={value => handleUserValues(value, 'imperial')}
                verticalAlignment="center"
              />
            </div>
            <div className="radio-group">
              <Radio
                testId="user_metric"
                labelElement={<span>{METRIC}</span>}
                value="1"
                selected={profileFormUser.metric}
                onChange={value => handleUserValues(value, 'metric')}
                verticalAlignment="center"
              />
            </div>
          </div>
        </div>
        <div className="clear clearfix bottom-section">
          <label className="bold-label string optional">{RECORDING_PREFERENCE}</label>
          <div className="radio-container">
            <div className="radio-group">
              <Radio
                testId="user_speed"
                labelElement={<span>{SPEED}</span>}
                value="1"
                selected={profileFormUser.displaySpeed}
                onChange={value => handleUserValues(value, 'displaySpeed')}
                verticalAlignment="center"
              />
            </div>
            <div className="radio-group">
              <Radio
                testId="user_pace"
                labelElement={<span>{PACE}</span>}
                value="1"
                selected={!profileFormUser.displaySpeed}
                onChange={value => handleUserValues(value, 'pace')}
                verticalAlignment="center"
              />
            </div>
          </div>
        </div>
        {calorieDiv()}
        {renderLanguageBlock()}
        {passwordDiv()}
        <div className="submit-buttons">
          <Button
            text={CANCEL}
            linkInfo={{
              href: LanguageSupportUtil.wrapUrlSafe('/my/profile', languageRegionCode)
            }}
            testId="cancel-edit-profile"
            variant="flat"
          />
          <Button text={SAVE} onClick={handleSubmit} type="submit" testId="submit-edit-profile" disabled={!recaptchaToken} variant="primary" />
        </div>
        {formErrorMessage}
      </div>
    </div>
  );
};

// needed to bypass error related to translations
// Could not find required `intl` object. <IntlProvider> needs to exist in the component ancestry
export default function ProfileEditFormWrapper({ context, currentUser, user, activities, enabledLanguages, defaultLatLng, mobileBrowser }: Props) {
  return (
    <CustomProvider>
      <ProfileEditForm
        context={context}
        currentUser={currentUser}
        user={user}
        activities={activities}
        enabledLanguages={enabledLanguages}
        defaultLatLng={defaultLatLng}
        mobileBrowser={mobileBrowser}
      />
    </CustomProvider>
  );
}
